import {
  Layers,
  Inbox,
  Database,
  Server,
  Code2,
  Sparkles,
  ShieldCheck,
  Lock,
  Network,
  Coins,
  CheckCircle2,
  Clock,
  Mail,
  ArrowRight,
} from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { JsonLd } from '@/components/JsonLd';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';
import { SITE_ORIGIN, HREFLANG_TAGS, SITE_LOCALE } from '@/lib/site-config';
import { buttonVariants } from '@/components/ui/Button';

const STACK_ICONS = {
  ingest: Inbox,
  queue: Layers,
  storage: Database,
  metadata: Server,
  frontend: Code2,
  ai: Sparkles,
} as const;

type StackKey = keyof typeof STACK_ICONS;
const STACK_KEYS: StackKey[] = ['ingest', 'queue', 'storage', 'metadata', 'frontend', 'ai'];

const ROADMAP_ITEMS = [
  { key: 'gdpr', tone: 'positive' as const },
  { key: 'soc2', tone: 'progress' as const },
  { key: 'iso', tone: 'tracking' as const },
  { key: 'uptime', tone: 'planned' as const },
  { key: 'benchmarks', tone: 'planned' as const },
];

const TONE_STYLES: Record<string, string> = {
  positive: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  progress: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  tracking: 'text-sky-300 bg-sky-500/10 border-sky-500/30',
  planned: 'text-gray-300 bg-gray-500/10 border-gray-500/30',
};

function StackCard({ stackKey, t }: { stackKey: StackKey; t: (key: string) => string }) {
  const Icon = STACK_ICONS[stackKey];
  return (
    <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 hover:border-primary-500/30 transition-colors">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-primary-400" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
            {t(`architecture.stack.${stackKey}.label`)}
          </p>
          <h3 className="text-base font-semibold text-white">
            {t(`architecture.stack.${stackKey}.value`)}
          </h3>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3 font-mono">
        {t(`architecture.stack.${stackKey}.location`)}
      </p>
      <p className="text-sm text-gray-400 leading-relaxed">
        {t(`architecture.stack.${stackKey}.description`)}
      </p>
    </div>
  );
}

function PrincipleSection({
  icon: Icon,
  title,
  subtitle,
  items,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  items: string[];
}) {
  return (
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
            <Icon className="h-6 w-6 text-primary-400" />
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{title}</h2>
            <p className="text-gray-400 leading-relaxed">{subtitle}</p>
          </div>
        </div>
        <ul className="space-y-3 ml-0 sm:ml-16">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-gray-300 leading-relaxed">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function ArchitecturePage() {
  const t = useT();

  useDocumentMeta({
    title: t('architecture.meta.title'),
    description: t('architecture.meta.description'),
  });

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('architecture.meta.title'),
    description: t('architecture.meta.description'),
    url: `${SITE_ORIGIN}/architecture`,
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: SITE_ORIGIN,
    },
  };

  const tenancyItems = ['item1', 'item2', 'item3', 'item4'].map((k) =>
    t(`architecture.tenancy.${k}`),
  );
  const retentionItems = ['item1', 'item2', 'item3', 'item4'].map((k) =>
    t(`architecture.retention.${k}`),
  );
  const aiItems = ['item1', 'item2', 'item3', 'item4'].map((k) => t(`architecture.ai.${k}`));

  return (
    <div className="min-h-screen bg-gray-950">
      <JsonLd data={pageJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6">
            <Layers className="h-4 w-4" />
            {t('architecture.hero.badge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('architecture.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {t('architecture.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Stack at a glance */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {t('architecture.stack.title')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('architecture.stack.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {STACK_KEYS.map((key) => (
              <StackCard key={key} stackKey={key} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Data flow */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {t('architecture.dataflow.title')}
            </h2>
            <p className="text-gray-400">{t('architecture.dataflow.subtitle')}</p>
          </div>
          <div className="rounded-2xl bg-gray-900/50 border border-gray-800 p-6 sm:p-8 font-mono text-xs sm:text-sm text-gray-300 leading-relaxed overflow-x-auto">
            <pre className="whitespace-pre">{`  Your apps  ──OTLP HTTP──▶  Fastify ingest  ──▶  BullMQ (Redis)
                                  │                       │
                              auth + quota              batch
                                  │                       ▼
                                  │             ClickHouse (GCP · Amsterdam)
                                  │                       │
                                  ▼                       │
                              PostgreSQL                  │
                              (metadata)                  │
                                                          ▼
  Web UI  ◀── LPQL / AI ──  Fastify query  ──▶  ClickHouse (tenant-scoped)`}</pre>
          </div>
          <p className="text-sm text-gray-500 mt-6 max-w-3xl mx-auto leading-relaxed">
            {t('architecture.dataflow.caption')}
          </p>
        </div>
      </section>

      {/* Multi-tenancy */}
      <PrincipleSection
        icon={Network}
        title={t('architecture.tenancy.title')}
        subtitle={t('architecture.tenancy.subtitle')}
        items={tenancyItems}
      />

      {/* Retention & cost */}
      <PrincipleSection
        icon={Coins}
        title={t('architecture.retention.title')}
        subtitle={t('architecture.retention.subtitle')}
        items={retentionItems}
      />

      {/* AI */}
      <PrincipleSection
        icon={Sparkles}
        title={t('architecture.ai.title')}
        subtitle={t('architecture.ai.subtitle')}
        items={aiItems}
      />

      {/* Roadmap / honesty */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-medium mb-4">
              <Clock className="h-3.5 w-3.5" />
              {t('architecture.roadmap.badge')}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {t('architecture.roadmap.title')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('architecture.roadmap.subtitle')}
            </p>
          </div>
          <div className="space-y-3">
            {ROADMAP_ITEMS.map(({ key, tone }) => (
              <div
                key={key}
                className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 p-5 rounded-xl bg-gray-900/50 border border-gray-800"
              >
                <div className="sm:w-44 flex-shrink-0">
                  <h3 className="text-base font-semibold text-white">
                    {t(`architecture.roadmap.${key}.title`)}
                  </h3>
                  <span
                    className={`inline-flex mt-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full border ${TONE_STYLES[tone]}`}
                  >
                    {t(`architecture.roadmap.${key}.status`)}
                  </span>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed sm:pt-1">
                  {t(`architecture.roadmap.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="rounded-3xl bg-gradient-to-br from-primary-500/10 to-primary-500/5 border border-primary-500/20 p-8 sm:p-12">
            <div className="w-14 h-14 rounded-2xl bg-primary-500/10 flex items-center justify-center mx-auto mb-5">
              <ShieldCheck className="h-7 w-7 text-primary-400" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('architecture.cta.title')}
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed">{t('architecture.cta.body')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="mailto:security@logpulse.io"
                className={buttonVariants({ variant: 'gradient', size: 'lg' })}
              >
                <Mail className="h-4 w-4 mr-2" />
                {t('architecture.cta.button')}
              </a>
              <LocalizedAnchor
                href="/security"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
              >
                <Lock className="h-4 w-4" />
                {t('architecture.cta.securityLink')}
                <ArrowRight className="h-3.5 w-3.5" />
              </LocalizedAnchor>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
